/**
 * Tech color assignment — deterministic hash fallback with admin override.
 *
 * The Jobs calendar cards need to visually distinguish which tech is on
 * each job. Admin can pick a color per tech in Settings → Techs
 * (persisted as `tech.color` on the doc). If they haven't picked one,
 * we derive a stable color from the tech name hash so the same tech
 * always renders in the same color.
 */

// Auto-palette for deterministic hash assignment. All entries are high
// contrast against white cards and AA-legible with white text.
// NOTE: the app's primary brand green (#207024) is intentionally EXCLUDED
// from this list so a tech never accidentally hashes to a color that
// blends with the app chrome. Admins can still pick it explicitly via
// the Tech dialog color picker (which references TECH_COLOR_PALETTE).
const AUTO_PALETTE = [
  { bg: "#2563eb", fg: "#ffffff" }, // blue
  { bg: "#c026d3", fg: "#ffffff" }, // fuchsia
  { bg: "#ea580c", fg: "#ffffff" }, // orange
  { bg: "#0891b2", fg: "#ffffff" }, // teal
  { bg: "#dc2626", fg: "#ffffff" }, // red
  { bg: "#65a30d", fg: "#ffffff" }, // lime
  { bg: "#7c3aed", fg: "#ffffff" }, // violet
  { bg: "#0d9488", fg: "#ffffff" }, // emerald
  { bg: "#b45309", fg: "#ffffff" }, // amber
];

// Manual picker palette — includes brand green as an explicit choice.
const PALETTE = [
  { bg: "#207024", fg: "#ffffff" },
  ...AUTO_PALETTE,
];

const hashString = (s) => {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = (h * 31 + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
};

/**
 * Look up a tech's color. Prefers the admin-set `color` field on the tech doc,
 * falls back to a deterministic hash of the tech's name.
 *
 * @param {string} techName - The tech's display name (e.g. "Brett").
 * @param {Array<{name?: string, color?: string}>} techs - Optional list of
 *   tech docs to consult for admin overrides. Match is by name (case-
 *   insensitive, trimmed) since the calendar carries `assignedEmployee`
 *   which is the name string.
 */
export const getTechColor = (techName, techs = []) => {
  if (!techName) return { bg: "#71717a", fg: "#ffffff" }; // zinc-500 unassigned
  const key = techName.trim().toLowerCase();
  const match = (techs || []).find(
    (t) => (t?.name || "").trim().toLowerCase() === key
  );
  if (match?.color && /^#[0-9a-f]{6}$/i.test(match.color)) {
    return { bg: match.color, fg: "#ffffff" };
  }
  return AUTO_PALETTE[hashString(key) % AUTO_PALETTE.length];
};

export const TECH_COLOR_PALETTE = PALETTE;
